import { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import ArrowSpinner from "@/components/ui/arrow-spinner";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isRecovering: boolean;
}

/**
 * Detects stale dynamic-import / chunk-load errors that happen after a deploy
 * (or when the user navigates while the new bundle is still rolling out).
 * These manifest as: "Failed to fetch dynamically imported module", "ChunkLoadError",
 * or "Loading chunk X failed".
 */
const isChunkLoadError = (error: Error | null): boolean => {
  if (!error) return false;
  const msg = (error.message || "").toLowerCase();
  const name = (error.name || "").toLowerCase();
  return (
    name === "chunkloaderror" ||
    msg.includes("failed to fetch dynamically imported module") ||
    msg.includes("loading chunk") ||
    msg.includes("loading css chunk") ||
    msg.includes("importing a module script failed")
  );
};

const RELOAD_FLAG = "arrowlink_chunk_reload_at";

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, isRecovering: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, isRecovering: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);

    // Auto-recover from stale-chunk errors with a single, throttled reload.
    if (isChunkLoadError(error)) {
      try {
        const last = Number(sessionStorage.getItem(RELOAD_FLAG) || 0);
        const now = Date.now();
        // Only reload once per 30s to avoid loops if the new bundle is also broken.
        if (now - last > 30_000) {
          sessionStorage.setItem(RELOAD_FLAG, String(now));
          // Bust any service worker / HTTP cache that might still be holding the stale chunk
          if ("caches" in window) {
            caches.keys().then((keys) => keys.forEach((k) => caches.delete(k))).catch(() => {});
          }
          // Small delay so the spinner gets a chance to render once
          setTimeout(() => window.location.reload(), 300);
        }
      } catch {
        setTimeout(() => window.location.reload(), 300);
      }
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, isRecovering: false });
  };

  render() {
    if (this.state.hasError) {
      // Stale-chunk path — show the branded spinner while we auto-reload.
      if (this.state.isRecovering) {
        return (
          <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
            <ArrowSpinner size="xl" className="text-primary animate-pulse" />
            <p className="text-sm text-muted-foreground">Loading the latest version…</p>
          </div>
        );
      }
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
          <div className="text-center max-w-md space-y-4">
            <h2 className="text-xl font-semibold text-foreground">Something went wrong</h2>
            <p className="text-muted-foreground text-sm">
              A temporary loading error occurred. This usually resolves by retrying.
            </p>
            <div className="flex gap-3 justify-center">
              <Button onClick={this.handleRetry}>Try Again</Button>
              <Button variant="outline" onClick={() => window.location.reload()}>
                Reload Page
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
